import React, { FC, useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import ScaleIcon from '@mui/icons-material/Scale';
import DialogTitle from '@mui/material/DialogTitle';
import NumbersIcon from '@mui/icons-material/Numbers';
import SetMealIcon from '@mui/icons-material/SetMeal';
import CategoryIcon from '@mui/icons-material/Category';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import ClearIcon from "@mui/icons-material/ClearOutlined";
import SignLanguage from '@mui/icons-material/SignLanguage';
import DescriptionIcon from '@mui/icons-material/Description';
import DialogContentText from '@mui/material/DialogContentText';
import TakeoutDiningIcon from '@mui/icons-material/TakeoutDining';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import { Autocomplete, Box, Button, Chip, FormLabel, IconButton, InputAdornment, Modal, TextField, Tooltip, Typography } from '@mui/material';
import WeightUnit from '../../../../Models/Interfaces/WeightUnit';
import Certificate from '../../../../Models/Interfaces/Certificate';
import TarriffCode from '../../../../Models/Interfaces/TarriffCode';
import ActionTypes from '../../../../Constants/ActionTypes';
import ReferenceData from '../../../../Models/Interfaces/ReferenceData';
import ProductSubmit from '../../../../Models/SubmitModels/ProductSubmit';
import ProductErrorModel from '../../../../Models/ErrorModels/ProductErrorModel';
import { addProduct, editProduct, getProcessingPlants, getFishTypeCNCodes } from '../../../../Services/VeidivottordAPIService';
import { AddProductTitle, InputFieldContainer, InputRow, ProductModalWrapper, StyledBox } from './ProductModal.styled';
import ProcessingPlant from '../../../../Models/Interfaces/ProcessingPlant';
import CertificateTypes from '../../../../Constants/CertificateTypes';
import CNCodeTreeSelect from './CNCodeTreeSelect';
import FishType from '../../../../Models/Interfaces/FishType';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import GridOnIcon from '@mui/icons-material/GridOn';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

interface ProductModalProps {
  toggleProductModal: (actionType?: string) => void;
  productModalOpen: boolean;
  referenceData: ReferenceData | undefined;
  selectedCertificate: Certificate | null;
  openSnackbar: (statusCode: number) => void;
  setLoadingState: (loadingState: boolean) => void;
  fetchCertificate: (certificateId: number) => void;
  productId?: number;
  modalActionType?: string | undefined;
}

const ProductModal: FC<ProductModalProps> = (props) => {

  const [productFormData, setProductFormData] = useState<ProductSubmit>({} as ProductSubmit);
  const [processingPlants, setProcessingPlants] = useState<ProcessingPlant[]>([]);
  const [defaultWeightUnit, setDefaultWeightUnit] = useState<WeightUnit | undefined | null>(null);
  const [errorModel, setErrorModel] = useState<ProductErrorModel>({} as ProductErrorModel);
  const [confirmWeightUnitEditDialogOpen, setConfirmWeightUnitEditDialogOpen] = useState(false);
  const [weightUnitEditModalOpen, setWeightUnitEditModalOpen] = useState(false);
  const [fishTypeCNCodes, setFishTypeCNCodes] = useState<FishType[]>([]);
  const [processingStatementContainers, setProcessingStatementContainers] = useState<string[]>([]);
  const [processingStatementSealMap, setProcessingStatementSealMap] = useState<Record<string, string>>({});

  const FISHTYPE_DETAIL_DESCRIPTION_MAX_LENGTH = 100;
  const TARRIFF_CODE_MAX_LENGTH = 20;

  const certificateContainers = useMemo(() => (
    (props.selectedCertificate?.containerNumber || '')
      .split(/[,;\n\r]+/)
      .map(container => container.trim().toUpperCase())
      .filter(Boolean)
  ), [props.selectedCertificate?.containerNumber]);

  const persistProcessingStatementTransport = (containers: string[], sealMap: Record<string, string>) => {
    const sealValues = containers.map(container => (sealMap[container] || '').trim().toUpperCase());
    handleProductChange("processingStatementContainerNumber", containers.length ? containers.join(';') : null);
    handleProductChange("processingStatementSealNumber", sealValues.some(Boolean) ? sealValues.join(';') : null);
  };

  const setProcessingStatementTransport = (containers: string[], sealMap: Record<string, string>) => {
    setProcessingStatementContainers(containers);
    setProcessingStatementSealMap(sealMap);
    persistProcessingStatementTransport(containers, sealMap);
  };

  const toggleProcessingStatementContainer = (container: string) => {
    const nextContainers = processingStatementContainers.includes(container)
      ? processingStatementContainers.filter(item => item !== container)
      : [...processingStatementContainers, container];
    const nextSealMap = nextContainers.reduce<Record<string, string>>((acc, item) => {
      acc[item] = processingStatementSealMap[item] || '';
      return acc;
    }, {});
    setProcessingStatementTransport(nextContainers, nextSealMap);
  };

  const handleProcessingStatementSealChange = (container: string, value: string) => {
    const nextSealMap = { ...processingStatementSealMap, [container]: value.toUpperCase() };
    setProcessingStatementTransport(processingStatementContainers, nextSealMap);
  };

  const normalizeSearchValue = (value: string) =>
    value
      .toLowerCase()
      .replace(/[.\-_,/\\]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const getSearchTokens = (value: string) =>
    normalizeSearchValue(value)
      .split(' ')
      .filter(Boolean);

  const getProcessingPlantSearchValue = (plant: ProcessingPlant) =>
    normalizeSearchValue([
      plant.processingPlantName,
      plant.processingPlantAddress,
      plant.processingPlantPostalCode,
      plant.processingPlantApprovalNumber,
    ].filter(Boolean).join(' '));

  const openConfirmWeightUnitEditDialog = () => {
    setConfirmWeightUnitEditDialogOpen(true);
  };

  const handleCloseWeightUnitEditDialog = () => {
    setConfirmWeightUnitEditDialogOpen(false);
  };

  useEffect(() => {

    // Reset error model.
    setErrorModel({} as ProductErrorModel);

    // If productId is set and it's a copy/edit action, copy product data from selected product.
    if (props?.productId && (props?.modalActionType === ActionTypes.COPY_ACTION || props?.modalActionType === ActionTypes.EDIT_ACTION)) {
      const productToCopy = props.selectedCertificate?.products.find(product => product.id === props.productId);
      if (productToCopy) {
        handleProductChange("tarriffCode", productToCopy.tarriffCode?.code);
        handleProductChange("fishTypeDetailedDescription", productToCopy?.fishType?.description ? productToCopy.fishType.description : "");
        handleProductChange("fishTypeId", productToCopy.fishType?.id);
        handleProductChange("processTypeId", productToCopy.processType?.id);
        handleProductChange("packagingTypeId", productToCopy.packagingType?.id);
        handleProductChange("packageAmount", productToCopy.packageAmount);
        handleProductChange("nettoWeight", productToCopy.nettoWeight);
        handleProductChange("weightUnitId", productToCopy.weightUnit?.id);
        handleProductChange("catchProcessedKg", productToCopy.catchProcessedKg);
        handleProductChange("processingPlantId", productToCopy.processingPlant ? productToCopy.processingPlant.id : undefined);
        handleProductChange("cnCodeCC", productToCopy?.tarriffCode?.cnCodeCC);
        handleProductChange("cnCodePS", productToCopy?.tarriffCode?.cnCodePS);
        handleProductChange("isProcessed", productToCopy.catchProcessedKg != null && productToCopy.catchProcessedKg > 0);
        const productContainers = productToCopy.processingStatementContainerNumber
          ?.split(/[,;\n\r]+/)
          .map(container => container.trim().toUpperCase())
          .filter(Boolean) ?? [];
        const productSeals = productToCopy.processingStatementSealNumber
          ?.split(';')
          .map(seal => seal.trim().toUpperCase()) ?? [];
        const sealMap = productContainers.reduce<Record<string, string>>((acc, container, index) => {
          acc[container] = productSeals[index] || '';
          return acc;
        }, {});
        const allowedContainers = new Set(certificateContainers);
        const filteredContainers = productContainers.filter(container => allowedContainers.has(container));
        const filteredSealMap = filteredContainers.reduce<Record<string, string>>((acc, container) => {
          acc[container] = sealMap[container] || '';
          return acc;
        }, {});
        setProcessingStatementTransport(filteredContainers, filteredSealMap);
      }
    } else {
      setProductFormData({} as ProductSubmit);
      setProcessingStatementTransport([], {});
      handleProductChange("isProcessed", true);
      findDefaultWeightUnit();
    }
  }, [props.selectedCertificate, props.productId, props.modalActionType, certificateContainers]);

  useEffect(() => {
    fetchProcessingPlants();
  }, []);

  const findDefaultWeightUnit = () => {
    if (props.selectedCertificate?.products?.length) {
      // Choose the same weight unit as first product in certificate.
      const weightUnit = props.selectedCertificate?.products[0].weightUnit;
      setDefaultWeightUnit(weightUnit);
      handleProductChange("weightUnitId", weightUnit?.id);
    } else {
      // Default selection is kg.
      const weightUnit = props.referenceData?.weightUnits.find(weightUnit => weightUnit.id === 2);
      setDefaultWeightUnit(weightUnit);
      handleProductChange("weightUnitId", weightUnit?.id);
    }
  }

  const handleProductChange = (name: string, value: string | number | boolean | null | undefined | any[] | object) => {
    setProductFormData((prevFormData) => ({ ...prevFormData, [name]: value }));

    if (name === 'fishTypeId') {
      // Fish type code is needed to fetch CN codes.
      const selectedFishType = props.referenceData?.fishTypes.find(fishType => fishType.id === Number(value));
      getFishTypeCNCodes(selectedFishType?.code ?? 0).then(codes => {
        setFishTypeCNCodes(codes);
      });

      // Clear CN codes when fish type changes.
      handleProductChange("cnCodeCC", null);
      handleProductChange("cnCodePS", null);
    }
  };

  const allFieldsExist = () => {
    if (props.selectedCertificate?.typeId === CertificateTypes.EU) {
      // For EU certificates
      if (productFormData?.isProcessed) {
        // Processed product: require cnCodeCC and cnCodePS
        return (
          productFormData?.cnCodeCC &&
          productFormData?.cnCodePS &&
          productFormData?.fishTypeId &&
          productFormData?.nettoWeight &&
          productFormData?.weightUnitId &&
          productFormData?.catchProcessedKg &&
          productFormData?.processingPlantId
        ) ? true : false;
      } else {
        // Not processed: require only cnCodeCC
        return (
          productFormData?.cnCodeCC &&
          productFormData?.fishTypeId &&
          productFormData?.nettoWeight &&
          productFormData?.weightUnitId
        ) ? true : false;
      }
    } else {
      // For other certificates, require tarriffCode
      return (
        productFormData?.tarriffCode &&
        productFormData?.fishTypeDetailedDescription &&
        productFormData?.fishTypeId &&
        productFormData?.processTypeId &&
        productFormData?.packagingTypeId &&
        productFormData?.packageAmount &&
        productFormData?.nettoWeight &&
        productFormData?.weightUnitId
      ) ? true : false;
    }
  }

  const autoSelectTarriffValues = (selectedTarriffCode: TarriffCode | undefined | null) => {

    if (selectedTarriffCode === undefined || selectedTarriffCode === null) {
      return;
    }

    let fishTypeFound = false;
    let processTypeFound = false;

    let fishTypeCount = 0;
    props.referenceData?.fishTypes.forEach(fishType => {
      if (selectedTarriffCode?.description.toLowerCase().includes(fishType.name.toLowerCase()) &&
        !selectedTarriffCode?.description.toLowerCase().includes("other than")) {
        fishTypeCount++;
      }
    });

    // Only set fish type if exactly one match is found.
    if (fishTypeCount === 1) {
      props.referenceData?.fishTypes.forEach(fishType => {
        if (selectedTarriffCode?.description.toLowerCase().includes(fishType.name.toLowerCase()) &&
          !selectedTarriffCode?.description.toLowerCase().includes("other than")) {
          setProductFormData((prevFormData) => ({ ...prevFormData, 'fishTypeId': fishType.id }));
          fishTypeFound = true;
        }
      });
    }
    props.referenceData?.processTypes.forEach(processType => {
      if (selectedTarriffCode?.description.toLowerCase().includes(processType.name.toLowerCase())) {
        setProductFormData((prevFormData) => ({ ...prevFormData, 'processTypeId': processType.id }));
        processTypeFound = true;
      }
    });

    // Autofocus next input field.
    if (fishTypeFound && processTypeFound) {
      const packagingTypeInput = document.getElementById('packagingTypeId');
      packagingTypeInput?.focus();
    } else if (fishTypeFound) {
      const processTypeInput = document.getElementById('processTypeId');
      processTypeInput?.focus();
    } else {
      const fishTypeInput = document.getElementById('fishTypeId');
      fishTypeInput?.focus();
    }
  }

  const handleSubmitProduct = async () => {
    // Warn user about weight unit change.
    if (props.selectedCertificate?.products?.length && productFormData.weightUnitId !== props.selectedCertificate?.products[0]?.weightUnit?.id) {
      openConfirmWeightUnitEditDialog();
    } else {
      submitProduct();
    }
  };

  const handleEditProduct = async () => {
    // Warn user about weight unit change.
    if (productFormData.weightUnitId !== props.selectedCertificate?.products.find(product => product.id === props.productId)?.weightUnit?.id) {
      openConfirmWeightUnitEditDialog();
    } else {
      submitEditProduct();
    }
  };

  const submitEditProduct = async () => {
    handleCloseWeightUnitEditDialog();
    props.toggleProductModal();

    if (productFormData?.isProcessed === false) {
      // If product is marked as not processed, clear processed fields.
      productFormData.catchProcessedKg = undefined;
      productFormData.processingPlantId = null;
      productFormData.cnCodePS = undefined;
      productFormData.processingStatementContainerNumber = null;
      productFormData.processingStatementSealNumber = null;
      setProcessingStatementTransport([], {});
    }
    
    const response = await editProduct(props.setLoadingState, productFormData, props.productId);
    props.fetchCertificate(props.selectedCertificate?.id ? props.selectedCertificate.id : 0);
    props.openSnackbar(response.status);
  };

  const submitProduct = async () => {
    handleCloseWeightUnitEditDialog();
    props.toggleProductModal();
    const response = await addProduct(props.setLoadingState, productFormData, props.selectedCertificate?.id);
    props.fetchCertificate(props.selectedCertificate?.id ? props.selectedCertificate.id : 0);
    props.openSnackbar(response.status);
  }

  const fetchProcessingPlants = async () => {
    const processingPlants = await getProcessingPlants(props.setLoadingState);
    setProcessingPlants(processingPlants);
  }

  return (
    <ProductModalWrapper>

      <Modal
        open={props.productModalOpen}
        onClose={() => {
          if (props.modalActionType === ActionTypes.EDIT_ACTION || props.modalActionType === ActionTypes.COPY_ACTION) {
            props.toggleProductModal();
          } else {
            if (Object.entries(productFormData).some(([key, value]) => {
              return key !== 'weightUnitId' && (value !== null && value !== '');
            })) {
              if (window.confirm('Ertu viss um að þú viljir hætta við?')) {
                props.toggleProductModal();
              }
            } else {
              props.toggleProductModal();
            }
          }
        }}
      >
        <StyledBox>

          <AddProductTitle>
            {props.modalActionType === ActionTypes.COPY_ACTION ? "Afrita vöru" : props.modalActionType === ActionTypes.EDIT_ACTION ? "Breyta vöru" : "Bæta við vöru"}
            <Chip
              variant="outlined"
              color="primary"
              label="Vöruupplýsingar"
            />
          </AddProductTitle>

          <InputFieldContainer>

            {(props.selectedCertificate?.typeId === CertificateTypes.USA || props.selectedCertificate?.typeId === CertificateTypes.JAPAN) && (
              <React.Fragment>
                {/* Vöruupplýsingar */}
                <InputRow>
                  <Autocomplete
                    disablePortal
                    onChange={(e, value) => {
                      if (typeof value !== 'string') {
                        handleProductChange("tarriffCode", value?.code);
                        handleProductChange("fishTypeDetailedDescription", value?.description);
                        autoSelectTarriffValues(value);
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      if (typeof newInputValue === 'string') {
                        handleProductChange("tarriffCode", newInputValue);
                      }
                    }}
                    value={
                      productFormData?.tarriffCode
                        ? productFormData.tarriffCode
                        : null
                    }
                    options={props.referenceData?.tarriffCodes ? props.referenceData.tarriffCodes : []}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') {
                        return option;
                      } else {
                        return option.code + " - " + option.description
                      }
                    }}
                    filterOptions={(options, { inputValue }) => {
                      const searchValue = inputValue.replace(/\./g, '');
                      return options.filter(option =>
                        option.code.replace(/\./g, '').toLowerCase().includes(searchValue.toLowerCase()) ||
                        option.description.replace(/\./g, '').toLowerCase().includes(searchValue.toLowerCase())
                      );
                    }}
                    sx={{ width: '100%' }}
                    clearOnBlur={false}
                    freeSolo
                    renderInput={(params) => <TextField
                      label="Tollskrárnúmer"
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        required: true,
                        startAdornment: (
                          <InputAdornment position='start'>
                            <NumbersIcon />
                          </InputAdornment>
                        )
                      }}
                      onChange={(e) => {
                        handleProductChange("tarriffCode", e.target.value);
                        setErrorModel({
                          ...errorModel, tarriffCode: {
                            error: e.target.value?.length > TARRIFF_CODE_MAX_LENGTH ? true : false,
                            message: e.target.value?.length > TARRIFF_CODE_MAX_LENGTH ? 'Tollskrárnúmer má ekki vera lengra en 20 stafir.' : '',
                          }
                        });
                      }}
                      error={errorModel?.tarriffCode?.error}
                      helperText={errorModel?.tarriffCode?.message}
                      required
                    />
                    }
                  />
                </InputRow>

                <InputRow>
                  <TextField
                    onChange={(e) => {
                      handleProductChange("fishTypeDetailedDescription", e.target.value);
                      setErrorModel({
                        ...errorModel, fishTypeDetailedDescription: {
                          error: e.target.value?.length > FISHTYPE_DETAIL_DESCRIPTION_MAX_LENGTH ? true : false,
                          message: e.target.value?.length > FISHTYPE_DETAIL_DESCRIPTION_MAX_LENGTH ? 'Lýsing má ekki vera lengra en 100 stafir.' : '',
                        }
                      });
                    }}
                    value={
                      productFormData?.fishTypeDetailedDescription
                        ? productFormData?.fishTypeDetailedDescription
                        : ""
                    }
                    sx={{ width: '100%' }}
                    label="Nánari lýsing"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DescriptionIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <IconButton onClick={() => handleProductChange("fishTypeDetailedDescription", "")} edge="end">
                          {productFormData?.fishTypeDetailedDescription ? <ClearIcon fontSize='small'></ClearIcon> : ''}
                        </IconButton>
                      )
                    }}
                    error={errorModel?.fishTypeDetailedDescription?.error}
                    helperText={errorModel?.fishTypeDetailedDescription?.message}
                    autoComplete='off'
                    required
                  />
                </InputRow>

                <InputRow>
                  <Autocomplete
                    disablePortal
                    id="fishTypeId"
                    sx={{ width: '100%' }}
                    options={props.referenceData?.fishTypes ?? []}
                    value={props.referenceData?.fishTypes.find(
                      fishType => fishType.id === productFormData?.fishTypeId
                    ) ?? null}
                    onChange={(_, value) => {
                      handleProductChange("fishTypeId", value?.id);
                    }}
                    getOptionLabel={(option) => option.name + " (" + option.scientificName + ")"}
                    slotProps={{
                      popper: { disablePortal: false }
                    }}
                    // Disable if editing and has linked landings
                    disabled={props.modalActionType === ActionTypes.EDIT_ACTION &&
                      Boolean(props.selectedCertificate?.landings?.find(
                        landing => landing.productId === props.productId
                      ))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Fisktegund"
                        required
                        disabled={props.modalActionType === ActionTypes.EDIT_ACTION &&
                          Boolean(props.selectedCertificate?.landings?.find(
                            landing => landing.productId === props.productId
                          ))}
                        title={props.modalActionType === ActionTypes.EDIT_ACTION &&
                          props.selectedCertificate?.landings?.find(
                            landing => landing.productId === props.productId
                          )
                          ? "Ekki er leyfilegt að breyta fisktegund ef landanatenging hefur átt sér stað."
                          : undefined}
                        InputProps={{
                          ...params.InputProps,
                          required: true,
                          startAdornment: (
                            <InputAdornment position='start'>
                              <SetMealIcon />
                            </InputAdornment>
                          )
                        }}
                      />
                    )}
                  />
                </InputRow>

                <InputRow>
                  <Autocomplete
                    disablePortal
                    onChange={(e, value) => handleProductChange("processTypeId", value?.id)}
                    value={
                      productFormData?.processTypeId
                        ? props.referenceData?.processTypes.find(processType => processType.id === productFormData?.processTypeId)
                        : null
                    }
                    slotProps={{
                      popper: {
                        disablePortal: false,
                      }
                    }}
                    options={props.referenceData?.processTypes ? props.referenceData.processTypes : []}
                    getOptionLabel={(option) => option.name}
                    sx={{ width: '100%' }}
                    id="processTypeId"
                    renderInput={(params) => <TextField
                      label="Vinnsla"
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        required: true,
                        startAdornment: (
                          <InputAdornment position='start'>
                            <SignLanguage />
                          </InputAdornment>
                        )
                      }}
                      required
                    />
                    }
                  />
                </InputRow>

                <InputRow>
                  <Autocomplete
                    disablePortal
                    onChange={(e, value) => handleProductChange("packagingTypeId", value?.id)}
                    value={
                      productFormData?.packagingTypeId
                        ? props.referenceData?.packagingTypes.find(packagingType => packagingType.id === productFormData?.packagingTypeId)
                        : null
                    }
                    slotProps={{
                      popper: {
                        disablePortal: false,
                      }
                    }}
                    options={props.referenceData?.packagingTypes ? props.referenceData.packagingTypes : []}
                    getOptionLabel={(option) => option.name}
                    sx={{ width: '100%' }}
                    id='packagingTypeId'
                    renderInput={(params) => <TextField
                      label="Pakkningar"
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        required: true,
                        startAdornment: (
                          <InputAdornment position='start'>
                            <TakeoutDiningIcon />
                          </InputAdornment>
                        )
                      }}
                      required
                    />
                    }
                  />
                </InputRow>

                <InputRow>
                  <TextField
                    onChange={(e) => {
                      handleProductChange("packageAmount", e.target.value)
                      if (Number(e.target.value) <= 0) {
                        setErrorModel({
                          ...errorModel, packageAmount: {
                            error: true,
                            message: 'Fjöldi pakkninga þarf að vera fleiri en núll.',
                          }
                        });
                      } else if (!Number.isInteger(Number(e.target.value))) {
                        setErrorModel({
                          ...errorModel, packageAmount: {
                            error: true,
                            message: 'Aðeins heiltölur leyfðar.',
                          }
                        });
                      } else {
                        setErrorModel({
                          ...errorModel, packageAmount: {
                            error: false,
                            message: '',
                          }
                        });
                      }
                    }}
                    value={
                      productFormData?.packageAmount
                        ? productFormData.packageAmount
                        : ''
                    }
                    sx={{ width: '100%' }}
                    label="Fjöldi pakkninga"
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CategoryIcon />
                        </InputAdornment>
                      ),
                      inputProps: { min: 0 },
                    }}
                    error={errorModel?.packageAmount?.error}
                    helperText={errorModel?.packageAmount?.message}
                    autoComplete='off'
                    required
                  />
                </InputRow>

                <InputRow>
                  <TextField
                    type="number"
                    onChange={(e) => {
                      handleProductChange("nettoWeight", e.target.value)
                      setErrorModel({
                        ...errorModel, nettoWeight: {
                          error: Number(e.target.value) <= 0 ? true : false,
                          message: Number(e.target.value) <= 0 ? 'Nettóþyngd þarf að vera hærri en núll.' : '',
                        }
                      });
                    }}
                    value={
                      productFormData?.nettoWeight
                        ? productFormData.nettoWeight
                        : ''
                    }
                    sx={{ width: '100%' }}
                    label="Nettóþyngd vöru"
                    onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ScaleIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          <Typography
                            component="span"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mx: 1,
                              fontWeight: 'medium',
                              color: 'text.secondary',
                            }}
                          >
                            {props.referenceData?.weightUnits.find(unit => unit.id === productFormData?.weightUnitId)?.name || defaultWeightUnit?.name || ''}
                          </Typography>
                          <Tooltip title="Breyta þyngdareiningu">
                            <IconButton
                              onClick={() => setWeightUnitEditModalOpen(true)}
                              edge="end"
                              size="small"
                              sx={{ color: 'action.active' }}
                              tabIndex={-1}
                            >
                              <EditIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        </React.Fragment>
                      ),
                      inputProps: { min: 0 },
                    }}
                    error={errorModel?.nettoWeight?.error}
                    helperText={errorModel?.nettoWeight?.message}
                    autoComplete='off'
                    required
                  />

                  <Dialog
                    open={weightUnitEditModalOpen}
                    onClose={() => setWeightUnitEditModalOpen(false)}
                    maxWidth="xs"
                    fullWidth
                  >
                    <DialogTitle>Breyta þyngdareiningu</DialogTitle>
                    <DialogContent>
                      <DialogContentText sx={{ mb: 2 }}>
                        Athugið að breyting á þyngdareiningu hefur áhrif á allar vörur tengdar þessu vottorði.
                      </DialogContentText>
                      <Autocomplete
                        disablePortal
                        onChange={(e, value) => {
                          handleProductChange("weightUnitId", value?.id)
                          setWeightUnitEditModalOpen(false);
                        }}
                        options={props.referenceData?.weightUnits ? props.referenceData.weightUnits : []}
                        value={props.referenceData?.weightUnits.find(unit => unit.id === productFormData?.weightUnitId) || null}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        sx={{ width: '100%' }}
                        slotProps={{
                          popper: {
                            disablePortal: false,
                            style: { zIndex: 1500 }
                          }
                        }}
                        renderInput={(params) => <TextField
                          label="Þyngdareining"
                          {...params}
                          InputProps={{
                            ...params.InputProps,
                            required: true,
                            startAdornment: (
                              <InputAdornment position='start'>
                                <ScaleIcon />
                              </InputAdornment>
                            )
                          }}
                          required
                        />}
                      />
                    </DialogContent>
                  </Dialog>
                </InputRow>

              </React.Fragment>
            )}

            {(props.selectedCertificate?.typeId === CertificateTypes.EU) && (
              <React.Fragment>

                <Box>
                  <FormLabel
                    sx={{
                      fontSize: 14,
                      color: 'text.primary',
                      fontWeight: 600,
                      letterSpacing: 0.2,
                      borderBottom: '2px solid #1976d2',
                      pb: 0.5,
                      mb: 1,
                      display: 'inline-block'
                    }}
                  >
                    Tegund vöru
                  </FormLabel>
                </Box>
                <InputRow>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      width: '100%',
                      justifyContent: 'stretch',
                      mt: 0.5,
                      mb: 0.5,
                      flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    }}
                  >
                    {/* Unnin vara */}
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1.2,
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        boxShadow: productFormData?.isProcessed ? '0 1px 4px rgba(25,118,210,0.10)' : 'none',
                        background: productFormData?.isProcessed
                          ? 'linear-gradient(180deg, #f5faff 0%, #f0f7ff 100%)'
                          : 'background.paper',
                        fontWeight: productFormData?.isProcessed ? 600 : 500,
                        fontSize: '0.95rem',
                        minHeight: 40,
                        outline: 'none',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                          transform: 'translateY(-1px)',
                        },
                        '&:focus-visible': {
                          borderColor: 'primary.main',
                          boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                          transform: 'translateY(-1px)',
                        }
                      }}
                      onClick={() => handleProductChange('isProcessed', true)}
                      tabIndex={0}
                      role="button"
                      aria-label="Veldu unnin vöru"
                      aria-pressed={productFormData?.isProcessed === true}
                      onKeyDown={e => {
                        if (e.key === ' ' || e.key === 'Enter') {
                          e.preventDefault();
                          handleProductChange('isProcessed', true);
                        }
                      }}
                    >
                      <span style={{ flex: 1, lineHeight: 1.15, fontSize: '0.95rem' }}>Veiðivottorð + vinnsluvottorð</span>
                      {productFormData?.isProcessed === true && (
                        <CheckIcon sx={{ ml: 1, color: 'primary.main', fontSize: 18 }} />
                      )}
                    </Box>
                    {/* Óunnin vara */}
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1.2,
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        boxShadow: productFormData?.isProcessed === false ? '0 1px 4px rgba(25,118,210,0.10)' : 'none',
                        background: productFormData?.isProcessed === false
                          ? 'linear-gradient(180deg, #f5faff 0%, #f0f7ff 100%)'
                          : 'background.paper',
                        fontWeight: productFormData?.isProcessed === false ? 600 : 500,
                        fontSize: '0.95rem',
                        minHeight: 40,
                        outline: 'none',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                          transform: 'translateY(-1px)',
                        },
                        '&:focus-visible': {
                          borderColor: 'primary.main',
                          boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                          transform: 'translateY(-1px)',
                        }
                      }}
                      onClick={() => handleProductChange('isProcessed', false)}
                      tabIndex={0}
                      role="button"
                      aria-label="Veldu óunna vöru"
                      aria-pressed={productFormData?.isProcessed === false}
                      onKeyDown={e => {
                        if (e.key === ' ' || e.key === 'Enter') {
                          e.preventDefault();
                          handleProductChange('isProcessed', false);
                        }
                      }}
                    >
                      <span style={{ flex: 1, lineHeight: 1.15, fontSize: '0.95rem' }}>Veiðivottorð</span>
                      {productFormData?.isProcessed === false && (
                        <CheckIcon sx={{ ml: 1, color: 'primary.main', fontSize: 18 }} />
                      )}
                    </Box>
                  </Box>
                </InputRow>

                {/* Vöruupplýsingar */}
                <InputRow>
                  <Autocomplete
                    disablePortal
                    id="fishTypeId"
                    sx={{ width: '100%' }}
                    options={props.referenceData?.fishTypes ?? []}
                    value={props.referenceData?.fishTypes.find(
                      fishType => fishType.id === productFormData?.fishTypeId
                    ) ?? null}
                    onChange={(_, value) => handleProductChange("fishTypeId", value?.id)}
                    getOptionLabel={(option) => option.name + " (" + option.scientificName + ")"}
                    slotProps={{
                      popper: { disablePortal: false }
                    }}
                    // Disable if editing and has linked landings
                    disabled={props.modalActionType === ActionTypes.EDIT_ACTION &&
                      Boolean(props.selectedCertificate?.landings?.find(
                        landing => landing.productId === props.productId
                      ))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Fisktegund"
                        required
                        disabled={props.modalActionType === ActionTypes.EDIT_ACTION &&
                          Boolean(props.selectedCertificate?.landings?.find(
                            landing => landing.productId === props.productId
                          ))}
                        title={props.modalActionType === ActionTypes.EDIT_ACTION &&
                          props.selectedCertificate?.landings?.find(
                            landing => landing.productId === props.productId
                          )
                          ? "Ekki er leyfilegt að breyta fisktegund ef landanatenging hefur átt sér stað."
                          : undefined}
                        InputProps={{
                          ...params.InputProps,
                          required: true,
                          startAdornment: (
                            <InputAdornment position='start'>
                              <SetMealIcon />
                            </InputAdornment>
                          )
                        }}
                      />
                    )}
                  />
                </InputRow>

                {/* USA and JAPAN */}
                {props.selectedCertificate?.typeId !== CertificateTypes.EU && (
                  <InputRow>
                    <Autocomplete
                      disablePortal
                      onChange={(e, value) => {
                        if (typeof value !== 'string') {
                          handleProductChange("tarriffCode", value?.cnCode);
                          handleProductChange("fishTypeDetailedDescription", value?.description);
                        }
                      }}
                      onInputChange={(event, newInputValue) => {
                        if (typeof newInputValue === 'string') {
                          handleProductChange("tarriffCode", newInputValue);
                        }
                      }}
                      value={
                        productFormData?.tarriffCode
                          ? productFormData.tarriffCode
                          : null
                      }
                      options={props.referenceData?.catchCertificateCNCodes ? props.referenceData.catchCertificateCNCodes : []}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') {
                          return option;
                        } else {
                          return option.cnCode + " - " + option.description
                        }
                      }}
                      sx={{ width: '100%' }}
                      clearOnBlur={false}
                      freeSolo
                      renderInput={(params) => <TextField
                        label="Tollskrárnúmer"
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          required: true,
                          startAdornment: (
                            <InputAdornment position='start'>
                              <NumbersIcon />
                            </InputAdornment>
                          )
                        }}
                        onChange={(e) => {
                          handleProductChange("tarriffCode", e.target.value);
                          setErrorModel({
                            ...errorModel, tarriffCode: {
                              error: e.target.value?.length > TARRIFF_CODE_MAX_LENGTH ? true : false,
                              message: e.target.value?.length > TARRIFF_CODE_MAX_LENGTH ? 'Tollskrárnúmer má ekki vera lengra en 20 stafir.' : '',
                            }
                          });
                        }}
                        error={errorModel?.tarriffCode?.error}
                        helperText={errorModel?.tarriffCode?.message}
                        required
                      />
                      }
                    />
                  </InputRow>
                )}

                {/* EU */}
                {props.selectedCertificate?.typeId === CertificateTypes.EU && (
                  <InputRow>

                    <CNCodeTreeSelect
                      options={
                        (() => {
                          const allCodes = props.referenceData?.catchCertificateCNCodes ?? [];

                          const allowedSet = new Set(
                            fishTypeCNCodes?.[0]?.catchCertificateCNCodes?.map(fc => fc.cnCode) ?? []
                          );

                          const fishTypeSelected = allowedSet.size > 0;

                          if (!fishTypeSelected) {
                            return allCodes.map(c => ({ ...c, allowedSelection: true }));
                          }

                          const getSegments = (c: any): string[] => {
                            if (Array.isArray(c.path)) return c.path.filter(Boolean);
                            if (typeof c.path === "string") return c.path.split("/").filter(Boolean);
                            return [];
                          };

                          const keepPathKeys = new Set<string>();

                          for (const code of allCodes) {
                            if (!allowedSet.has(code.cnCode)) continue;

                            const segs = getSegments(code);
                            for (let i = 1; i <= segs.length; i++) {
                              keepPathKeys.add(segs.slice(0, i).join("/"));
                            }

                            if (segs.length === 0 && code.cnCode) {
                              keepPathKeys.add(code.cnCode);
                            }
                          }

                          return allCodes
                            .filter(code => {
                              const segs = getSegments(code);
                              const key = segs.length ? segs.join("/") : (code.cnCode ?? "");
                              return keepPathKeys.has(key);
                            })
                            .map(code => ({
                              ...code,
                              allowedSelection: allowedSet.has(code.cnCode)
                            }));
                        })()
                      }
                      disabled={productFormData?.fishTypeId == null}
                      value={productFormData?.cnCodeCC ?? null}
                      onChange={(cnCode, description) => {
                        handleProductChange("cnCodeCC", cnCode);
                        handleProductChange("tarriffCode", cnCode);
                        handleProductChange("fishTypeDetailedDescription", description ?? "");
                      }}
                    />
                  </InputRow>
                )}

                {!productFormData?.isProcessed && (
                  <InputRow>
                    <TextField
                      onChange={(e) => {
                        handleProductChange("fishTypeDetailedDescription", e.target.value);
                        setErrorModel({
                          ...errorModel, fishTypeDetailedDescription: {
                            error: e.target.value?.length > FISHTYPE_DETAIL_DESCRIPTION_MAX_LENGTH ? true : false,
                            message: e.target.value?.length > FISHTYPE_DETAIL_DESCRIPTION_MAX_LENGTH ? 'Lýsing má ekki vera lengra en 100 stafir.' : '',
                          }
                        });
                      }}
                      value={
                        productFormData?.fishTypeDetailedDescription
                          ? productFormData?.fishTypeDetailedDescription
                          : ""
                      }
                      sx={{ width: '100%' }}
                      label="Nánari lýsing"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <DescriptionIcon />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <IconButton onClick={() => handleProductChange("fishTypeDetailedDescription", "")} edge="end">
                            {productFormData?.fishTypeDetailedDescription ? <ClearIcon fontSize='small'></ClearIcon> : ''}
                          </IconButton>
                        )
                      }}
                      error={errorModel?.fishTypeDetailedDescription?.error}
                      helperText={errorModel?.fishTypeDetailedDescription?.message}
                      autoComplete='off'
                      required
                    />
                  </InputRow>
                )}

                <InputRow>
                  <TextField
                    type="number"
                    onChange={(e) => {
                      handleProductChange("nettoWeight", e.target.value)
                      setErrorModel({
                        ...errorModel, nettoWeight: {
                          error: Number(e.target.value) <= 0 ? true : false,
                          message: Number(e.target.value) <= 0 ? 'Nettóþyngd þarf að vera hærri en núll.' : '',
                        }
                      });
                    }}
                    value={
                      productFormData?.nettoWeight
                        ? productFormData.nettoWeight
                        : ''
                    }
                    sx={{ width: '100%' }}
                    label="Nettóþyngd vöru"
                    onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ScaleIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          <Typography
                            component="span"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mx: 1,
                              fontWeight: 'medium',
                              color: 'text.secondary',
                            }}
                          >
                            {props.referenceData?.weightUnits.find(unit => unit.id === productFormData?.weightUnitId)?.name || defaultWeightUnit?.name || ''}
                          </Typography>
                          <Tooltip title="Breyta þyngdareiningu">
                            <IconButton
                              onClick={() => setWeightUnitEditModalOpen(true)}
                              edge="end"
                              size="small"
                              sx={{ color: 'action.active' }}
                              tabIndex={-1}
                            >
                              <EditIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        </React.Fragment>
                      ),
                      inputProps: { min: 0 },
                    }}
                    error={errorModel?.nettoWeight?.error}
                    helperText={errorModel?.nettoWeight?.message}
                    autoComplete='off'
                    required
                  />

                  <Dialog
                    open={weightUnitEditModalOpen}
                    onClose={() => setWeightUnitEditModalOpen(false)}
                    maxWidth="xs"
                    fullWidth
                  >
                    <DialogTitle>Breyta þyngdareiningu</DialogTitle>
                    <DialogContent>
                      <DialogContentText sx={{ mb: 2 }}>
                        Athugið að breyting á þyngdareiningu hefur áhrif á allar vörur tengdar þessu vottorði.
                      </DialogContentText>
                      <Autocomplete
                        disablePortal
                        onChange={(e, value) => {
                          handleProductChange("weightUnitId", value?.id)
                          setWeightUnitEditModalOpen(false);
                        }}
                        options={props.referenceData?.weightUnits ? props.referenceData.weightUnits : []}
                        value={props.referenceData?.weightUnits.find(unit => unit.id === productFormData?.weightUnitId) || null}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        sx={{ width: '100%' }}
                        slotProps={{
                          popper: {
                            disablePortal: false,
                            style: { zIndex: 1500 }
                          }
                        }}
                        renderInput={(params) => <TextField
                          label="Þyngdareining"
                          {...params}
                          InputProps={{
                            ...params.InputProps,
                            required: true,
                            startAdornment: (
                              <InputAdornment position='start'>
                                <ScaleIcon />
                              </InputAdornment>
                            )
                          }}
                          required
                        />}
                      />
                    </DialogContent>
                  </Dialog>
                </InputRow>

                {productFormData?.isProcessed && (
                  <React.Fragment>
                    <Box
                      sx={{ mb: 1 }}
                    >
                      <FormLabel
                        sx={{
                          fontSize: 14,
                          color: 'text.primary',
                          fontWeight: 600,
                          letterSpacing: 0.2,
                          borderBottom: '2px solid #1976d2',
                          pb: 0.5,
                          mb: 1,
                          display: 'inline-block'
                        }}
                      >
                        Vinnsluupplýsingar
                      </FormLabel>
                    </Box>

                    <InputRow>
                      <CNCodeTreeSelect
                        options={
                          (() => {
                            const allCodes = props.referenceData?.processingStatementCNCodes ?? [];

                            const allowedSet = new Set(
                              fishTypeCNCodes?.[0]?.processingStatementCNCodes?.map(fc => fc.cnCode) ?? []
                            );

                            const fishTypeSelected = allowedSet.size > 0;

                            if (!fishTypeSelected) {
                              return allCodes.map(c => ({ ...c, allowedSelection: c.cnCode?.length === 8 }));
                            }

                            const getSegments = (c: any): string[] => {
                              if (Array.isArray(c.path)) return c.path.filter(Boolean);
                              if (typeof c.path === "string") return c.path.split("/").filter(Boolean);
                              return [];
                            };

                            const keepPathKeys = new Set<string>();

                            // Check if first 6 letters of any fishTypeCNCode matches processing statement CN code
                            const isAllowedByPrefix = (cnCode: string) => {
                              const fishTypeCodes = fishTypeCNCodes?.[0]?.processingStatementCNCodes ?? [];
                              return fishTypeCodes.some(fc => {
                                const fishTypePrefix = fc.cnCode?.substring(0, 6);
                                const codePrefix = cnCode?.substring(0, 6);
                                return fishTypePrefix === codePrefix;
                              });
                            };

                            for (const code of allCodes) {
                              if (!allowedSet.has(code.cnCode) && !isAllowedByPrefix(code.cnCode)) continue;

                              const segs = getSegments(code);
                              for (let i = 1; i <= segs.length; i++) {
                                keepPathKeys.add(segs.slice(0, i).join("/"));
                              }

                              if (segs.length === 0 && code.cnCode) {
                                keepPathKeys.add(code.cnCode);
                              }
                            }

                            return allCodes
                              .filter(code => {
                                const segs = getSegments(code);
                                const key = segs.length ? segs.join("/") : (code.cnCode ?? "");
                                return keepPathKeys.has(key);
                              })
                              .map(code => ({
                                ...code,
                                allowedSelection: (allowedSet.has(code.cnCode) || isAllowedByPrefix(code.cnCode)) && code.cnCode?.length === 8
                              }));
                          })()
                        }
                        value={productFormData?.cnCodePS ?? null}
                        disabled={productFormData?.fishTypeId == null}
                        onChange={(cnCode, description) => {
                          handleProductChange("cnCodePS", cnCode);
                          handleProductChange("tarriffCode", cnCode);
                          handleProductChange("fishTypeDetailedDescription", description ?? "");
                        }}
                      />
                    </InputRow>

                    <InputRow>
                      <TextField
                        onChange={(e) => {
                          handleProductChange("fishTypeDetailedDescription", e.target.value);
                          setErrorModel({
                            ...errorModel, fishTypeDetailedDescription: {
                              error: e.target.value?.length > FISHTYPE_DETAIL_DESCRIPTION_MAX_LENGTH ? true : false,
                              message: e.target.value?.length > FISHTYPE_DETAIL_DESCRIPTION_MAX_LENGTH ? 'Lýsing má ekki vera lengra en 100 stafir.' : '',
                            }
                          });
                        }}
                        value={
                          productFormData?.fishTypeDetailedDescription
                            ? productFormData?.fishTypeDetailedDescription
                            : ""
                        }
                        sx={{ width: '100%' }}
                        label="Nánari lýsing"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <DescriptionIcon />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <IconButton onClick={() => handleProductChange("fishTypeDetailedDescription", "")} edge="end">
                              {productFormData?.fishTypeDetailedDescription ? <ClearIcon fontSize='small'></ClearIcon> : ''}
                            </IconButton>
                          )
                        }}
                        error={errorModel?.fishTypeDetailedDescription?.error}
                        helperText={errorModel?.fishTypeDetailedDescription?.message}
                        autoComplete='off'
                        required
                      />
                    </InputRow>

                    <InputRow>
                      <TextField
                        type="number"
                        onChange={(e) => {
                          handleProductChange("catchProcessedKg", e.target.value)
                          setErrorModel({
                            ...errorModel, catchProcessedKg: {
                              error: Number(e.target.value) < 0 ? true : false,
                              message: Number(e.target.value) < 0 ? 'Þarf að vera 0 eða hærra.' : '',
                            }
                          });
                        }}
                        value={
                          productFormData?.catchProcessedKg
                            ? productFormData.catchProcessedKg
                            : ''
                        }
                        sx={{ width: '100%' }}
                        label="Unnið magn af hrávöru"
                        onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <ScaleIcon />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <React.Fragment>
                              <Typography
                                component="span"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mx: 1,
                                  fontWeight: 'medium',
                                  color: 'text.secondary',
                                }}
                              >
                                kg
                              </Typography>
                            </React.Fragment>
                          ),
                          inputProps: { min: 0 },
                        }}
                        error={errorModel?.catchProcessedKg?.error}
                        helperText={errorModel?.catchProcessedKg?.message}
                        autoComplete='off'
                        required
                      />
                    </InputRow>

                    <InputRow style={{ paddingBottom: 0 }}>
                      <Box sx={{ width: '100%', mb: 2 }}>
                        <Box sx={{ display: 'flex', width: '100%', gap: 2, alignItems: 'flex-start' }}>
                          <Autocomplete
                            disablePortal
                            id="processingPlantId"
                            sx={{ width: '100%' }}
                            options={processingPlants}
                            getOptionKey={(option) => option.id}
                            getOptionLabel={(option) =>
                              typeof option === 'string'
                                ? option
                                : option.processingPlantName || ''
                            }
                            filterOptions={(options, { inputValue }) => {
                              const tokens = getSearchTokens(inputValue);
                              if (tokens.length === 0) {
                                return options;
                              }
                              return options.filter(option => {
                                const searchValue = getProcessingPlantSearchValue(option);
                                return tokens.every(token => searchValue.includes(token));
                              });
                            }}
                            renderOption={(props, option) => {
                              const { key, ...otherProps } = props;
                              return (
                                <li key={key} {...otherProps}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="body1" fontWeight="medium">
                                      {option.processingPlantName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {option.processingPlantAddress}, {option.processingPlantPostalCode} • {option.processingPlantApprovalNumber}
                                    </Typography>
                                    {option.healthCertificateNumber && option.healthCertificateDate && (
                                      <Typography variant="caption" color="text.secondary">
                                        {option.healthCertificateNumber} ({new Date(option.healthCertificateDate).toLocaleDateString('is-IS')})
                                      </Typography>
                                    )}
                                  </Box>
                                </li>
                              );
                            }}
                            value={
                              productFormData?.processingPlantId ?
                                processingPlants.find(plant => plant.id === productFormData?.processingPlantId) || null
                                : null
                            }
                            onChange={(_, value) => {
                              handleProductChange("processingPlantId", value?.id);
                            }}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                sx={{ paddingRight: '1em' }}
                                label="Veldu vinnslustöð"
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <InputAdornment position='start'>
                                      <WarehouseIcon />
                                    </InputAdornment>
                                  )
                                }}
                              />
                            )}
                          />
                        </Box>
                      </Box>
                    </InputRow>

                    {certificateContainers.length > 0 && (
                      <InputRow>
                        <Box
                          sx={{
                            width: '100%',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            backgroundColor: 'rgba(25,118,210,0.03)',
                            p: 1.25,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <GridOnIcon sx={{ color: 'primary.main', fontSize: 19 }} />
                            <FormLabel
                              sx={{
                                fontSize: 13,
                                color: 'text.primary',
                                fontWeight: 700,
                              }}
                            >
                              Gámar á vinnsluvottorði
                            </FormLabel>
                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                              Veldu þá gáma sem eiga við þessa vinnsluyfirlýsingu.
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {certificateContainers.map(container => {
                              const selected = processingStatementContainers.includes(container);
                              return (
                                <Chip
                                  key={container}
                                  label={container}
                                  color={selected ? 'primary' : 'default'}
                                  variant={selected ? 'filled' : 'outlined'}
                                  onClick={() => toggleProcessingStatementContainer(container)}
                                  icon={selected ? <CheckIcon /> : <GridOnIcon />}
                                  sx={{
                                    height: 34,
                                    fontWeight: selected ? 600 : 500,
                                    '& .MuiChip-icon': {
                                      color: selected ? 'inherit' : 'text.secondary'
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>
                          {processingStatementContainers.length > 0 && (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                mt: 0.5,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                pt: 1
                              }}
                            >
                              {processingStatementContainers.map(container => (
                                <Box
                                  key={container}
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'minmax(180px, 240px) 1fr' },
                                    gap: 1,
                                    alignItems: 'center'
                                  }}
                                >
                                  <Chip
                                    icon={<GridOnIcon />}
                                    label={container}
                                    color="success"
                                    variant="outlined"
                                    sx={{
                                      height: 40,
                                      fontWeight: 600,
                                      backgroundColor: 'rgba(46,125,50,0.08)',
                                      borderColor: 'success.light',
                                      color: 'success.dark'
                                    }}
                                  />
                                  <TextField
                                    size="small"
                                    label="Innsiglisnúmer"
                                    value={processingStatementSealMap[container] || ''}
                                    onChange={(e) => handleProcessingStatementSealChange(container, e.target.value)}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <VpnKeyIcon fontSize="small" />
                                        </InputAdornment>
                                      ),
                                    }}
                                    inputProps={{ maxLength: 100 }}
                                    helperText="Valkvætt"
                                    autoComplete="off"
                                  />
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </InputRow>
                    )}

                  </React.Fragment>
                )}
              </React.Fragment>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
              <Box sx={{ flex: '1 1 auto' }} />
              <Tooltip
                title={
                  !allFieldsExist()
                    ? "Allar upplýsingar þurfa að vera fylltar út."
                    : !Object.values(errorModel).every(error => error.error === false)
                      ? "Vinsamlegast lagaðu villur."
                      : ""
                }
                arrow
              >
                <span>
                  <Button
                    onClick={props.modalActionType === ActionTypes.EDIT_ACTION ? handleEditProduct : handleSubmitProduct}
                    variant="contained"
                    disabled={!Object.values(errorModel).every(error => error.error === false) || !allFieldsExist()}
                  >
                    {props.modalActionType === ActionTypes.COPY_ACTION ? "Afrita vöru" : props.modalActionType === ActionTypes.EDIT_ACTION ? "Breyta vöru" : "Bæta við vöru"}
                  </Button>
                </span>
              </Tooltip>
            </Box>

          </InputFieldContainer>

        </StyledBox>
      </Modal>

      {
        confirmWeightUnitEditDialogOpen &&
        <Dialog
          open={confirmWeightUnitEditDialogOpen}
          onClose={handleCloseWeightUnitEditDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Ertu viss um að þú viljir breyta þyngdareiningu?"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Breyting á þyngdareiningu mun uppfæra allar vörur tengdar þessu vottorði.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseWeightUnitEditDialog}>Hætta við</Button>
            <Button onClick={() => props.modalActionType === ActionTypes.EDIT_ACTION ? submitEditProduct() : submitProduct()} autoFocus>
              Samþykkja
            </Button>
          </DialogActions>
        </Dialog>
      }

    </ProductModalWrapper >
  );
}

export default ProductModal;
